import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import ThriveTitle from "../../common/ThriveTitle";
import { StyledButtonMain } from "../../common/Buttons";
import ReminderEditModal from "../../Dashboard/Reminders/ReminderEditModal";
import {
  ActionItemAttachmentType,
  ActionItemTaskType,
  ActionItemType,
} from "../../../interfaces";

// The coaching-plan editor that replaced the retired Business Action Plan form:
// one goal + a task checklist, nothing administrative. The due date that drives
// the Today feed and the action_plan_due follow-up rule is the earliest
// incomplete task due_date — there is no timeLine countdown any more.
export const actionItemDueDate = (item: ActionItemType): Dayjs | null => {
  const tasks = Array.isArray(item.tasks) ? item.tasks : [];
  const due = tasks
    .filter((t) => !t.completed && t.due_date)
    .map((t) => dayjs(t.due_date));
  if (!due.length) return null;
  return due.reduce((a, b) => (a.isBefore(b) ? a : b));
};

// New task rows start with a due date a week out so every Action Item has
// something for the dashboard feed to sort and surface by; the user can still
// change or clear it in the DatePicker below. Serialized exactly the way a
// picked date is (`dayjs(...).toDate()` — a JS Date), so the save payload shape
// is identical whether the date was defaulted or chosen.
export const DEFAULT_TASK_DUE_DAYS = 7;

const defaultTaskDueDate = () =>
  dayjs().add(DEFAULT_TASK_DUE_DAYS, "day").startOf("day").toDate();

// Client-side guard only — the shared /upload/case-doc endpoint has no size
// limit of its own, so keep casual uploads from filling the case_doc volume.
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

const attachmentDownloadUrl = (case_id: string, a: ActionItemAttachmentType) =>
  `${process.env.REACT_APP_API_DOMAIN}/upload/case-doc/${case_id}/${a.storageName}/${encodeURIComponent(a.originalName)}`;

const newTaskId = () =>
  typeof crypto !== "undefined" && (crypto as any).randomUUID
    ? (crypto as any).randomUUID()
    : `t_${Date.now()}_${Math.random().toString(36).slice(2)}`;

type ActionItemEditModalProps = {
  setOpen: (open: boolean) => void;
  actionItem: ActionItemType;
  setActionItem: (actionItem: ActionItemType) => void;
  handleSave: (
    pendingFiles: File[],
    removedAttachments: ActionItemAttachmentType[]
  ) => void | Promise<void>;
  handleComplete: (
    pendingFiles: File[],
    removedAttachments: ActionItemAttachmentType[]
  ) => void | Promise<void>;
  handleDelete: () => void;
  saving?: boolean;
};

const ActionItemEditModal: React.FC<ActionItemEditModalProps> = ({
  setOpen,
  actionItem,
  setActionItem,
  handleSave,
  handleComplete,
  handleDelete,
  saving = false,
}) => {
  const [reminderPromptDue, setReminderPromptDue] = useState<Dayjs | null>(null);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  // Files chosen but not uploaded yet — the parent uploads them on save so a
  // cancelled edit never leaves orphan bytes on disk.
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [removedAttachments, setRemovedAttachments] = useState<
    ActionItemAttachmentType[]
  >([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tasks: ActionItemTaskType[] = Array.isArray(actionItem.tasks)
    ? actionItem.tasks
    : [];

  const updateTask = (index: number, patch: Partial<ActionItemTaskType>) => {
    setActionItem({
      ...actionItem,
      tasks: tasks.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    });
  };

  const addTask = () => {
    setActionItem({
      ...actionItem,
      tasks: [
        ...tasks,
        {
          task_id: newTaskId(),
          title: "",
          due_date: defaultTaskDueDate(),
          completed: false,
          created_at: new Date(),
        },
      ],
    });
  };

  const removeTask = (index: number) => {
    setActionItem({
      ...actionItem,
      tasks: tasks.filter((_, i) => i !== index),
    });
  };

  const existingAttachments: ActionItemAttachmentType[] = (
    Array.isArray(actionItem.attachments) ? actionItem.attachments : []
  ).filter(
    (a) => !removedAttachments.some((r) => r.storageName === a.storageName)
  );

  const handlePickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(e.target.files ?? []);
    const tooBig = chosen.filter((f) => f.size > MAX_ATTACHMENT_BYTES);
    const ok = chosen.filter((f) => f.size <= MAX_ATTACHMENT_BYTES);
    if (tooBig.length) {
      setFileError(
        `Too large (max 25 MB): ${tooBig.map((f) => f.name).join(", ")}`
      );
    } else {
      setFileError("");
    }
    if (ok.length) {
      setPendingFiles((prev) => [
        ...prev,
        ...ok.filter(
          (f) => !prev.some((p) => p.name === f.name && p.size === f.size)
        ),
      ]);
    }
    e.target.value = "";
  };

  const removeExistingAttachment = (a: ActionItemAttachmentType) => {
    setRemovedAttachments((prev) => [...prev, a]);
    setActionItem({
      ...actionItem,
      attachments: (Array.isArray(actionItem.attachments)
        ? actionItem.attachments
        : []
      ).filter((x) => x.storageName !== a.storageName),
    });
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // An item with neither a goal nor a named task would be filtered out by the
  // server's ACTION_ITEM_MATCH and become invisible — block that save.
  // Attachments deliberately do not count: they cannot keep a plan visible.
  const isEmpty =
    !(actionItem.goal ?? "").trim() &&
    !tasks.some((t) => (t.title ?? "").trim());

  const handleSaveWithReminder = () => {
    handleSave(pendingFiles, removedAttachments);
    const due = actionItemDueDate(actionItem);
    setReminderPromptDue(due && due.isValid() ? due : null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", pb: 3 }}>
      <ThriveTitle title={"Add/Edit Action Item"} setOpen={setOpen}></ThriveTitle>

      <Box sx={{ padding: "24px 24px 0" }}>
        <TextField
          fullWidth
          label="Goal"
          placeholder="e.g. Open Food Business"
          value={actionItem.goal ?? ""}
          onChange={(e) =>
            setActionItem({ ...actionItem, goal: e.target.value })
          }
          sx={{ marginBottom: "16px" }}
        />

        <Typography sx={{ fontWeight: "bold", marginBottom: "8px" }}>
          Tasks
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={1}>
            {tasks.map((task, index) => (
              <Stack
                key={task.task_id}
                direction="row"
                spacing={1}
                sx={{ alignItems: "center" }}
              >
                <TextField
                  size="small"
                  label={`Task ${index + 1}`}
                  placeholder="e.g. Meet SBDC"
                  value={task.title}
                  onChange={(e) => updateTask(index, { title: e.target.value })}
                  sx={{ flexGrow: 1 }}
                />
                <DatePicker
                  label="Due"
                  value={task.due_date ? dayjs(task.due_date) : null}
                  onChange={(value: any) =>
                    updateTask(index, {
                      due_date: value ? dayjs(value).toDate() : null,
                    })
                  }
                  slotProps={{ textField: { size: "small" } }}
                />
                <IconButton
                  aria-label="remove task"
                  color="error"
                  onClick={() => removeTask(index)}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Stack>
            ))}
            {tasks.length === 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No tasks yet.
              </Typography>
            )}
            <Box>
              <Button
                startIcon={<AddIcon />}
                onClick={addTask}
                variant="outlined"
                size="small"
              >
                Add Task
              </Button>
            </Box>
          </Stack>
        </LocalizationProvider>

        <Typography sx={{ fontWeight: "bold", margin: "20px 0 8px" }}>
          Attachments
        </Typography>
        <Stack spacing={1}>
          {existingAttachments.map((a) => (
            <Stack
              key={a.storageName}
              direction="row"
              spacing={1}
              sx={{ alignItems: "center" }}
            >
              <AttachFileIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Link
                href={attachmentDownloadUrl(actionItem.case_id, a)}
                target="_blank"
                rel="noreferrer"
                sx={{ flexGrow: 1, cursor: "pointer", wordBreak: "break-all" }}
              >
                {a.originalName}
              </Link>
              <IconButton
                aria-label="remove attachment"
                color="error"
                size="small"
                onClick={() => removeExistingAttachment(a)}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Stack>
          ))}
          {pendingFiles.map((f, index) => (
            <Stack
              key={`${f.name}_${f.size}_${index}`}
              direction="row"
              spacing={1}
              sx={{ alignItems: "center" }}
            >
              <AttachFileIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography sx={{ flexGrow: 1, wordBreak: "break-all" }}>
                {f.name}
              </Typography>
              <Chip label="new" size="small" />
              <IconButton
                aria-label="remove pending attachment"
                color="error"
                size="small"
                onClick={() => removePendingFile(index)}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Stack>
          ))}
          {existingAttachments.length === 0 && pendingFiles.length === 0 && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No attachments.
            </Typography>
          )}
          {fileError && <Alert severity="warning">{fileError}</Alert>}
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handlePickFiles}
            />
            <Button
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              variant="outlined"
              size="small"
            >
              Attach file
            </Button>
          </Box>
        </Stack>
      </Box>

      {saving && <LinearProgress sx={{ mt: 2, mx: 3 }} />}

      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "flex-end", marginTop: "24px", px: 3 }}
      >
        <Button
          variant="contained"
          color="error"
          sx={{ mr: "auto !important" }}
          disabled={saving}
          onClick={handleDelete}
        >
          Delete
        </Button>
        <StyledButtonMain
          variant="contained"
          disabled={isEmpty || saving}
          sx={{ width: "140px" }}
          onClick={handleSaveWithReminder}
        >
          Save
        </StyledButtonMain>
        <StyledButtonMain
          variant="contained"
          disabled={saving}
          sx={{ width: "140px" }}
          onClick={() => handleComplete(pendingFiles, removedAttachments)}
        >
          Complete
        </StyledButtonMain>
      </Stack>

      {reminderPromptDue && (
        <Alert
          severity="info"
          sx={{ mt: 2, mx: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setReminderModalOpen(true)}
            >
              Remind me on the due date
            </Button>
          }
        >
          The next task is due {reminderPromptDue.format("MMM D, YYYY")}.
        </Alert>
      )}

      <ReminderEditModal
        open={reminderModalOpen}
        onClose={() => setReminderModalOpen(false)}
        onSaved={() => {
          setReminderModalOpen(false);
          setReminderPromptDue(null);
        }}
        initial={{
          title: `Action item due: ${actionItem.goal || ""}`,
          dueAt: reminderPromptDue ? reminderPromptDue.toDate() : undefined,
          case_id: actionItem.case_id,
          source: {
            kind: "action_plan",
            ...((actionItem._id && {
              action_plan_id: String(actionItem._id),
            }) ||
              {}),
          },
        }}
      />
    </Box>
  );
};

export default ActionItemEditModal;
